//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

export const CONTEXT_MESSAGE_SOURCE = 'devops-nexus' as const;
export const CONTEXT_MESSAGE_VERSION = 1 as const;

export type ContextLevel = 'teamspace' | 'loop';
export type ContextMessageType = 'CONTEXT_UPDATE' | 'CONTEXT_REQUEST';

export interface ContextPayload {
  // Teamspace Identity (Required)
  teamspaceId: string;
  teamspaceName?: string; // Display name for breadcrumbs/titles
  activeTeamspaceId?: string | null; // Active teamspace in this session (for cross-tab context)

  // Loop Context (Optional)
  loopId?: string | null;
  loopName?: string | null;

  // Derived
  level: ContextLevel;
}

export interface ContextMessage {
  source: typeof CONTEXT_MESSAGE_SOURCE;
  version: number;
  type: ContextMessageType;
  payload: ContextPayload;
}

/**
 * Derive level from payload fields (single source of truth).
 */
export function resolveLevel(loopId?: string | null): ContextLevel {
  return loopId ? 'loop' : 'teamspace';
}

/**
 * Build a frozen ContextMessage envelope.
 * Throws if teamspaceId is empty — it is a mandatory field.
 */
export function buildContextMessage(
  type: ContextMessageType,
  payload: Omit<ContextPayload, 'level'>,
): ContextMessage {
  if (!payload.teamspaceId) {
    throw new Error(
      'buildContextMessage: teamspaceId is required and must be non-empty',
    );
  }
  const message: ContextMessage = {
    source: CONTEXT_MESSAGE_SOURCE,
    version: CONTEXT_MESSAGE_VERSION,
    type,
    payload: {
      ...payload,
      level: resolveLevel(payload.loopId),
    },
  };
  return Object.freeze(message) as ContextMessage;
}

/**
 * Returns true only if the payload has a non-empty teamspaceId string
 * and a valid level field. Optional fields (teamspaceName, activeTeamspaceId, etc.)
 * are allowed but not required for validity.
 */
export function isValidPayload(p: unknown): p is ContextPayload {
  if (typeof p !== 'object' || p === null) return false;
  const obj = p as Record<string, unknown>;
  if (typeof obj.teamspaceId !== 'string' || obj.teamspaceId.length === 0) {
    return false;
  }
  if (obj.level !== 'teamspace' && obj.level !== 'loop') {
    return false;
  }
  return true;
}

/**
 * Resolve the effective allowlist: if the caller provided an explicit list,
 * use it; otherwise fall back to same-origin only.
 */
function getEffectiveOrigins(allowedOrigins: string[]): string[] {
  if (allowedOrigins.length > 0) return allowedOrigins;
  try {
    return [window.location.origin];
  } catch {
    return [];
  }
}

/**
 * Post a ContextMessage to an iframe using an explicit target origin taken
 * from the allowlist. If the iframe's origin is not in the allowlist, do
 * nothing and return false. Never uses '*'.
 */
export function sendMessageToIframe(
  iframe: HTMLIFrameElement | null,
  message: ContextMessage,
  allowedOrigins: string[],
): boolean {
  if (!iframe?.contentWindow) return false;

  const origins = getEffectiveOrigins(allowedOrigins);
  if (origins.length === 0) return false;

  // Determine the iframe's origin from its src attribute.
  let iframeOrigin: string;
  try {
    const src = iframe.src || iframe.getAttribute('src') || '';
    // Relative URLs resolve against the current page origin.
    const url = new URL(src, window.location.origin);
    iframeOrigin = url.origin;
  } catch {
    return false;
  }

  // Only post if the iframe origin is in the allowlist.
  const targetOrigin = origins.find((o) => o === iframeOrigin);
  if (!targetOrigin) return false;

  iframe.contentWindow.postMessage(message, targetOrigin);
  return true;
}

/**
 * Validate an inbound MessageEvent. Returns the message only if:
 *  - event.origin is in allowedOrigins (or same-origin when allowlist empty)
 *  - data.source === CONTEXT_MESSAGE_SOURCE and version matches
 *  - isValidPayload(data.payload)
 * Otherwise returns null. Never throws.
 */
export function parseContextMessage(
  event: MessageEvent,
  allowedOrigins: string[],
): ContextMessage | null {
  try {
    const origins = getEffectiveOrigins(allowedOrigins);
    if (!origins.includes(event.origin)) return null;

    const data = event.data;
    if (typeof data !== 'object' || data === null) return null;
    if (data.source !== CONTEXT_MESSAGE_SOURCE) return null;
    if (data.version !== CONTEXT_MESSAGE_VERSION) return null;
    if (!isValidPayload(data.payload)) return null;

    return data as ContextMessage;
  } catch {
    return null;
  }
}
